import type { TFunction } from "i18next";
import { useCallback } from "react";
import U2000SaveCancelDialog from "../../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000AutoCompleteInput from "../../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import { useU3121FindEmployeeHandlers } from "../../../../U3121UseFindHandlers";
import type { U3121SubProjectTimesheetAccessHandlerReturn } from "./U3121SubProjectTimesheetAccessHandlers";

interface U3121SubProjectTimesheetAccessEditDlgProps {
  handler: U3121SubProjectTimesheetAccessHandlerReturn;
  t: TFunction;
  NS: string;
}

function U3121SubProjectTimesheetAccessEditDlg({ handler, t, NS }: U3121SubProjectTimesheetAccessEditDlgProps) {
  const { formStateHandlers } = handler;
  const findEmployeeHandlers = useU3121FindEmployeeHandlers();
  const onSearchEmployee = useCallback(
    (criterias: { fieldName: string; value: unknown }[]) => findEmployeeHandlers.search(criterias),
    [findEmployeeHandlers],
  );

  const displayEmployee = useCallback((item: Record<string, unknown>) => {
    const firstName = (item.firstName as string) ?? "";
    const lastName = (item.lastName as string) ?? "";
    const codeEmployee = (item.codeEmployee as string) ?? "";
    return `${firstName} ${lastName} (${codeEmployee})`.trim() || String(item.id ?? "");
  }, []);

  const extractDataForEmployee = useCallback((result: Record<string, unknown> | null) => {
    if (result) {
      return [
        { fieldName: "id", value: result.id },
        { fieldName: "firstName", value: result.firstName },
        { fieldName: "lastName", value: result.lastName },
        { fieldName: "codeEmployee", value: result.codeEmployee },
      ];
    }
    return [
      { fieldName: "id", value: null },
      { fieldName: "firstName", value: null },
      { fieldName: "lastName", value: null },
      { fieldName: "codeEmployee", value: null },
    ];
  }, []);

  if (!handler.isDialogOpen) return null;

  return (
    <U2000SaveCancelDialog
      isOpen={handler.isDialogOpen}
      onClose={handler.onDlgClose}
      title={t("lblTimesheetAccess", { ns: NS })}
      isValid={!!formStateHandlers.getData()?.id?.trim()}
    >
      <div className="flex flex-col gap-y-4">
        <U2000AutoCompleteInput
          formStateHandlers={formStateHandlers}
          fieldName="id"
          mainFieldName="codeEmployee"
          entityLink="U3101"
          label={t("lblEmployee", { ns: NS })}
          onSearch={onSearchEmployee}
          displayFn={displayEmployee}
          overrideExtractDataFn={extractDataForEmployee}
          minCarLaunchSearch={1}
          resultLabels={[]}
          resultFields={["firstName", "lastName", "codeEmployee"]}
          required
        />
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U3121SubProjectTimesheetAccessEditDlg;
